import { h, Component, Fragment } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import { isEnabled as customerCreditEnabled } from '../../lib/customerCredit';

@observer
class DyrejournalReskontoMini extends Component {
    constructor(props) {
        super(props);
        this.state = {
            allSales: false,
            openOnly: false,
            paymentInProgress: {},
            credits: [],
        };
    }

    componentDidMount() {
        this.loadLedger();
        this.loadCredits();
    }

    visitorIds = () => {
        const { visitors = [] } = this.props;
        return visitors.map(v => v.id).filter(Boolean);
    }

    loadLedger = async () => {
        const ids = this.visitorIds();
        if (ids.length === 0) {
            return;
        }
        const { saleStore } = this.props.stores;
        await saleStore.loadCustomerLedger(ids, { allSales: this.state.allSales });
    }

    loadCredits = async () => {
        const ids = this.visitorIds();
        const { userStore, visitorCreditStore } = this.props.stores;
        if (ids.length === 0 || !customerCreditEnabled(userStore.customer)) {
            return;
        }
        const entries = await visitorCreditStore.load({
            query: { visitor: ids },
            skipUpdate: true,
            limit: 1000,
        });
        // API returns newest first; show oldest first with a running balance.
        const ascending = (Array.isArray(entries) ? entries : []).slice().reverse();
        let saldo = 0;
        const credits = ascending.map(e => {
            saldo = Math.round((saldo + (e.amount || 0) + Number.EPSILON) * 100) / 100;
            return { ...e, saldo };
        });
        this.setState({ credits });
    }

    toggleAllSales = async () => {
        this.setState({ allSales: !this.state.allSales }, this.loadLedger);
    }

    toggleOpenOnly = () => {
        this.setState({ openOnly: !this.state.openOnly });
    }

    loadSalePayments = async saleId => {
        if (!util.isNumber(saleId)) {
            return false;
        }
        const { salePaymentStore, saleStore } = this.props.stores;
        const response = await salePaymentStore.load({
            query: {
                sale: saleId,
            },
            skipUpdate: true,
            limit: 5000,
        });
        saleStore.updateKeyValue('currentPayments', response);
    }

    loadJournals = async (animalIds, sale, journalIds) => {
        const { journalStore, saleStore } = this.props.stores;
        const response = await journalStore.load({
            query: {
                id: journalIds,
            },
            skipUpdate: true,
        });
        saleStore.updateKeyValue('animalJournals', response);
    }

    openSale = async id => {
        if (!id) {
            return;
        }
        const { saleStore } = this.props.stores;
        const newSale = await saleStore.load(parseInt(id, 10), false, { skipUpdate: true });
        if (!newSale || !newSale[0]) {
            return;
        }
        if (newSale[0].animals && newSale[0].animals.length > 0) {
            const journalIds = (newSale[0].journals || []).map(e => e.id);
            const animalIds = newSale[0].animals.map(e => e.id);
            await this.loadJournals(animalIds, newSale[0].id, journalIds);
        }
        saleStore.createNewSale({ skipAnimalJournals: true, skipVisitorSales: true, skipVisitorAnimals: true });
        saleStore.updateKeyValue('newSale', newSale[0]);

        const { visitors } = newSale[0];
        const isProductionAnimals = visitors && visitors.some(v => v && v.producerNumber);
        saleStore.updateKeyValue('isProductionAnimals', isProductionAnimals);

        await this.loadSalePayments(parseInt(id, 10));
    }

    onClickOpenSale = async e => {
        e.preventDefault();
        e.stopPropagation();
        const id = parseInt(e.currentTarget.dataset.saleid, 10);
        if (!id) {
            return;
        }
        await this.openSale(id);
    }

    onClickRegisterPayment = async e => {
        e.preventDefault();
        e.stopPropagation();
        const id = parseInt(e.currentTarget.dataset.saleid, 10);
        if (!id) {
            return;
        }
        this.setState(prev => ({ paymentInProgress: { ...prev.paymentInProgress, [id]: true } }));
        const response = await util.fetchApi(`/api/sales/${id}/register-missing-payment`, { method: 'POST' }, {});
        if (response && response.data && response.data.created === true) {
            await this.loadLedger();
        } else {
            await this.openSale(id);
        }
        this.setState(prev => ({ paymentInProgress: { ...prev.paymentInProgress, [id]: false } }));
    }

    onClickPrint = () => {
        const ids = this.visitorIds();
        if (ids.length === 0) {
            return;
        }
        const apiServer = util.getApiServer();
        const jwtToken = util.getJwtToken();
        const params = ids.map(id => `visitor=${id}`).join('&');
        const allSalesParam = this.state.allSales ? '&allSales=1' : '';
        const url = `${apiServer}/api/sales/customer-ledger/pdf?${params}${allSalesParam}&jwtToken=${jwtToken}`;
        window.open(url, '_blank');
    }

    render() {
        const { classNames = '' } = this.props;
        const { allSales, openOnly, paymentInProgress, credits = [] } = this.state;
        const { saleStore } = this.props.stores;
        const { customerLedger, customerLedgerLoading } = saleStore;
        const summary = customerLedger && customerLedger.summary ? customerLedger.summary : {};
        let postings = customerLedger && customerLedger.postings ? customerLedger.postings : [];
        if (openOnly) {
            postings = postings.filter(p => p.type !== 'charge' || p.isOpen);
        }

        return (
            <div class={`d-flex flex-column ${classNames}`}>
                <div class='d-flex flex-row justify-content-between align-items-center px-2 py-1'>
                    <div>
                        <span class='fw-bold'>
                            <Text id='reskonto.balance'>Balance</Text>: {util.format(summary.saldo || 0, 2, ',', ' ')}
                        </span>
                        {summary.forfalt > 0 && (
                            <span class='text-danger ms-2'>
                                <Text id='reskonto.forfalt'>Overdue</Text>: {util.format(summary.forfalt, 2, ',', ' ')}
                            </span>
                        )}
                    </div>
                    <div class='d-flex flex-row align-items-center'>
                        <button
                            class={`btn btn-sm btn${openOnly ? '' : '-outline'}-secondary rounded-pill py-0 me-1`}
                            onClick={this.toggleOpenOnly}
                        >
                            <Text id='reskonto.openItems'>Open items only</Text>
                        </button>
                        <button
                            class={`btn btn-sm btn${allSales ? '' : '-outline'}-secondary rounded-pill py-0 me-1`}
                            onClick={this.toggleAllSales}
                        >
                            <Text id='reskonto.allSales'>All sales</Text>
                        </button>
                        <button class='btn btn-sm btn-outline-primary rounded-pill py-0' onClick={this.onClickPrint}>
                            <i class='fa-duotone fa-print' /> <Text id='reskonto.printStatement'>Print statement</Text>
                        </button>
                    </div>
                </div>

                {customerLedgerLoading && (
                    <div class='text-center text-body-secondary py-2'>
                        <Text id='reskonto.loading'>Loading ledger…</Text>
                    </div>
                )}

                {!customerLedgerLoading && postings.length === 0 && (
                    <div class='text-center text-body-secondary py-3'>
                        <Text id='reskonto.empty'>No receivables for this customer</Text>
                    </div>
                )}

                {postings.length > 0 && (
                    <table class='table table-sm table-hover mb-0'>
                        <thead>
                            <tr class='small text-body-secondary'>
                                <th><Text id='reskonto.date'>Date</Text></th>
                                <th><Text id='reskonto.voucher'>Voucher</Text></th>
                                <th><Text id='reskonto.text'>Text</Text></th>
                                <th><Text id='reskonto.dueDate'>Due date</Text></th>
                                <th class='text-end'><Text id='reskonto.debit'>Debit</Text></th>
                                <th class='text-end'><Text id='reskonto.credit'>Credit</Text></th>
                                <th class='text-end'><Text id='reskonto.balance'>Balance</Text></th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {postings.map((p, index) => {
                                const isCharge = p.type === 'charge';
                                const overdue = isCharge && p.isOverdue;
                                const saving = !!paymentInProgress[p.saleId];
                                return (
                                    <Fragment key={index}>
                                        <tr class={`small ${overdue ? 'text-danger' : ''}`}>
                                            <td><nobr>{util.isoDate(p.date, false, false, true)}</nobr></td>
                                            <td>
                                                {isCharge ? (
                                                    <a href='#' data-saleid={p.saleId} onClick={this.onClickOpenSale}>{p.bilag}</a>
                                                ) : p.bilag}
                                            </td>
                                            <td>{p.text}</td>
                                            <td>
                                                <nobr>{p.dueDate ? util.isoDate(p.dueDate, false, false, true) : ''}</nobr>
                                                {overdue && (
                                                    <span class='ms-1'>
                                                        (<Text id='reskonto.daysOverdue' fields={{ days: p.daysOverdue }}>{`${p.daysOverdue} days overdue`}</Text>)
                                                    </span>
                                                )}
                                            </td>
                                            <td class='text-end text-nowrap'>{p.debet > 0 ? util.format(p.debet, 2, ',', ' ') : ''}</td>
                                            <td class='text-end text-nowrap'>{p.kredit > 0 ? util.format(p.kredit, 2, ',', ' ') : ''}</td>
                                            <td class='text-end text-nowrap'>{util.format(p.saldo, 2, ',', ' ')}</td>
                                            <td class='text-end'>
                                                {isCharge && p.isOpen && (
                                                    <button
                                                        class='btn btn-sm btn-outline-success rounded-pill py-0'
                                                        data-saleid={p.saleId}
                                                        onClick={this.onClickRegisterPayment}
                                                        disabled={saving}
                                                    >
                                                        {saving
                                                            ? <i class='fa-duotone fa-spinner fa-spin' />
                                                            : <Text id='reskonto.registerPayment'>Register payment</Text>}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {(p.refs || []).map((r, ri) => (
                                            <tr key={`ref-${index}-${ri}`} class='small text-body-tertiary'>
                                                <td />
                                                <td colSpan='2' class='ps-3'>
                                                    &#8627; <Text id='reskonto.subInvoice' fields={{ number: r.saleNumber }}>{`Sub-invoice ${r.saleNumber}`}</Text>: {r.text}
                                                </td>
                                                <td />
                                                <td class='text-end text-nowrap'>{util.format(r.amount, 2, ',', ' ')}</td>
                                                <td /><td /><td />
                                            </tr>
                                        ))}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                )}

                {credits.length > 0 && (
                    <div class='mt-3'>
                        <div class='px-2 py-1 fw-bold'>
                            <i class='fa-solid fa-piggy-bank me-1' />
                            <Text id='reskonto.creditTitle'>Customer credit (kundekonto)</Text>:
                            {' '}{util.format(credits[credits.length - 1].saldo, 2, ',', ' ')}
                        </div>
                        <div class='table-responsive'>
                            <table class='table table-sm mb-0'>
                                <thead>
                                    <tr class='small text-body-secondary'>
                                        <th><Text id='reskonto.date'>Date</Text></th>
                                        <th><Text id='reskonto.voucher'>Voucher</Text></th>
                                        <th><Text id='reskonto.creditType'>Type</Text></th>
                                        <th class='text-end'><Text id='reskonto.amount'>Amount</Text></th>
                                        <th class='text-end'><Text id='reskonto.balance'>Balance</Text></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {credits.map(c => (
                                        <tr key={`credit-${c.id}`} class='small'>
                                            <td><nobr>{util.isoDate(c.date, false, false, true)}</nobr></td>
                                            <td>
                                                <a href='#' data-saleid={c.sale} onClick={this.onClickOpenSale}>{c.sale}</a>
                                            </td>
                                            <td>
                                                {c.type === 'return'
                                                    ? <Text id='reskonto.creditTypeReturn'>Return</Text>
                                                    : <Text id='reskonto.creditTypeUse'>Used</Text>}
                                            </td>
                                            <td class={`text-end text-nowrap ${c.amount < 0 ? 'text-danger' : 'text-success'}`}>{util.format(c.amount, 2, ',', ' ')}</td>
                                            <td class='text-end text-nowrap'>{util.format(c.saldo, 2, ',', ' ')}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default DyrejournalReskontoMini;
